import { ViewportObserver } from "../viewport";
import { ItemIdentifier, ItemType } from "~/parse/sfml-ast";
import { SFMLCullingElement } from "~/renderer/web-components/element";
import * as S from "~/styles/sfml.css";

import { UrlImageAnchor } from "./url-image-anchor";

export class UrlImage extends SFMLCullingElement {
  private readonly imageContainer: HTMLDivElement;
  private readonly imageElement: HTMLImageElement;

  static observedAttributes = ["url"];

  attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null) {
    switch (attr) {
      case "url": {
        if (newValue) this.draw(newValue);
        break;
      }
    }
  }

  public handleVisible() {
    super.handleVisible();

    const src = this.getAttribute("url");
    if (src) this.draw(src);
  }

  protected handleHidden() {
    super.handleHidden();
    this.draw("");
  }

  private draw(src: string) {
    const width = this.getAttribute("width");
    const height = this.getAttribute("height");

    this.imageElement.setAttribute("src", src);

    const areas = Array.from(this.getAreas());

    this.imageElement.onload = () => {
      for (let i = 0, n = areas.length; i < n; ++i) {
        const area = areas[i];
        if (area instanceof UrlImageAnchor) {
          const srcRect = area.getAttribute("src-rect");
          if (srcRect) {
            let [x, y, w, h] = srcRect
              .split(" ")
              .filter((x) => x.length > 0)
              .map((x) => Number(x));
            const a = document.createElement("a");

            const href = area.getAttribute("href");
            if (href !== null) {
              a.href = `${href}`;
            } else a.href = "#";

            const itemType = area.getAttribute("item-type") as ItemType;
            const itemId = area.getAttribute("source-id") as string;

            a.dataset.itemId = itemId;
            a.dataset.itemType = itemType;

            const label = area.getAttribute("label");
            if (label) a.setAttribute("aria-label", label);
            a.style.display = "block";
            a.style.position = "absolute";
            a.style.left = (x / Number(width!)) * 100 + "%";
            a.style.top = (y / Number(height!)) * 100 + "%";
            a.style.width = ((w + x) / Number(width!)) * 100 + "%";
            a.style.height = ((h + y) / Number(height!)) * 100 + "%";
            a.style.maxHeight = "100%";
            a.style.maxWidth = "100%";
            a.style.objectFit = "contain";
            this.imageContainer.appendChild(a);
            area.anchor = a;
          }
        }
      }
    };
  }

  constructor() {
    super();

    this.imageElement = document.createElement("img");
    this.imageElement.className = S.image;

    this.imageContainer = document.createElement("div");
    this.imageContainer.className = S.imageContainer;
    this.imageContainer.appendChild(this.imageElement);
    this.appendChild(this.imageContainer);
  }

  public getAreas(): UrlImageAnchor[] {
    const areas = this.querySelectorAll("sfml-url-image-a");
    const urlImageAnchors: UrlImageAnchor[] = [];
    for (let i = 0, n = areas.length; i < n; ++i) {
      const area = areas[i];

      if (area instanceof UrlImageAnchor) urlImageAnchors.push(area);
    }

    return urlImageAnchors;
  }

  public clientRects(): { identifier: ItemIdentifier; rect: ClientRect }[] {
    const areas = this.querySelectorAll("sfml-url-image-a");

    const rects: { identifier: ItemIdentifier; rect: ClientRect }[] = [];
    for (let i = 0, n = areas.length; i < n; ++i) {
      const area = areas[i];

      if (!(area instanceof UrlImageAnchor) || !area.anchor) {
        continue;
      }

      if (area.itemType === null) {
        continue;
      }

      if (area.itemId === null) {
        continue;
      }

      const rect = ViewportObserver.shared.getBoundingClientRect(area.anchor);

      const identifier = {
        itemType: area.itemType,
        itemId: area.itemId,
      };
      rects.push({ identifier, rect });
    }

    return rects;
  }
}
customElements.define("sfml-url-image", UrlImage);



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/image/url-image.ts